import { useEffect, useState, useRef, useCallback } from 'react';
import { api } from '../lib/api';
import type { LogFileInfo } from '../lib/api';
import { FileText, RefreshCw, Search, Trash2, ChevronDown, AlertCircle, Info, AlertTriangle, Bug } from 'lucide-react';
import { CollapsibleCard } from '../components/CollapsibleCard';

const LEVEL_FILTERS = ['ALL', 'ERROR', 'WARNING', 'INFO', 'DEBUG'] as const;
type LevelFilter = typeof LEVEL_FILTERS[number];

const LINE_OPTIONS = [100, 250, 500, 1000, 2000] as const;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function levelColor(line: string): string {
  if (/\[ERROR\]/.test(line)) return 'text-red-400';
  if (/\[WARNING\]/.test(line)) return 'text-yellow-400';
  if (/\[INFO\]/.test(line)) return 'text-emerald-400';
  if (/\[DEBUG\]/.test(line)) return 'text-gray-400';
  return 'text-gray-300';
}

function LevelBadge({ level }: { level: LevelFilter }) {
  const styles: Record<LevelFilter, string> = {
    ALL: 'bg-gray-700 text-gray-200',
    ERROR: 'bg-red-900 text-red-300',
    WARNING: 'bg-yellow-900 text-yellow-300',
    INFO: 'bg-emerald-900 text-emerald-300',
    DEBUG: 'bg-gray-800 text-gray-400',
  };
  const icons: Record<LevelFilter, React.ReactNode> = {
    ALL: <FileText className="w-3 h-3" />,
    ERROR: <AlertCircle className="w-3 h-3" />,
    WARNING: <AlertTriangle className="w-3 h-3" />,
    INFO: <Info className="w-3 h-3" />,
    DEBUG: <Bug className="w-3 h-3" />,
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${styles[level]}`}>
      {icons[level]} {level}
    </span>
  );
}

export function LogViewer() {
  const [files, setFiles] = useState<LogFileInfo[]>([]);
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [rawContent, setRawContent] = useState<string>('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState<LevelFilter>('ALL');
  const [maxLines, setMaxLines] = useState<number>(500);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [loadingContent, setLoadingContent] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const autoRefreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchFiles = useCallback(async () => {
    try {
      const data = await api.getLogFiles();
      setFiles(data.files);
      if (!selectedFile && data.files.length > 0) {
        setSelectedFile(data.files[0].filename);
      }
    } catch (err) {
      setError('Failed to load log files.');
    } finally {
      setLoadingFiles(false);
    }
  }, [selectedFile]);

  const fetchContent = useCallback(async (file: string, lines: number, srch: string) => {
    if (!file) return;
    setLoadingContent(true);
    setError(null);
    try {
      const data = await api.getLogContent(file, lines, srch || undefined);
      setRawContent(data.content);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load log content.');
      setRawContent('');
    } finally {
      setLoadingContent(false);
    }
  }, []);

  useEffect(() => { fetchFiles(); }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (selectedFile) fetchContent(selectedFile, maxLines, debouncedSearch);
  }, [selectedFile, maxLines, debouncedSearch, fetchContent]);

  useEffect(() => {
    if (autoRefreshTimer.current) clearInterval(autoRefreshTimer.current);
    if (autoRefresh && selectedFile) {
      autoRefreshTimer.current = setInterval(() => {
        fetchContent(selectedFile, maxLines, debouncedSearch);
      }, 10000);
    }
    return () => {
      if (autoRefreshTimer.current) clearInterval(autoRefreshTimer.current);
    };
  }, [autoRefresh, selectedFile, maxLines, debouncedSearch, fetchContent]);

  const visibleLines = rawContent
    ? rawContent
        .split('\n')
        .filter((ln) => {
          if (!ln.trim()) return false;
          if (levelFilter === 'ALL') return true;
          return ln.includes(`[${levelFilter}]`);
        })
    : [];

  const handleCleanup = async () => {
    setCleaningUp(true);
    setCleanupResult(null);
    try {
      const result = await api.triggerLogCleanup();
      setCleanupResult(
        result.count === 0
          ? 'No old log files to delete.'
          : `Deleted ${result.count} file(s): ${result.deleted_files.join(', ')}`
      );
      await fetchFiles();
    } catch {
      setCleanupResult('Cleanup failed.');
    } finally {
      setCleaningUp(false);
    }
  };

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const selectedFileInfo = files.find((f) => f.filename === selectedFile);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Application Logs</h1>
          <p className="text-sm text-gray-500 mt-1">
            Rolling daily log files — last 7 days retained automatically
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => fetchContent(selectedFile, maxLines, debouncedSearch)}
            disabled={loadingContent || !selectedFile}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loadingContent ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleCleanup}
            disabled={cleaningUp}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            {cleaningUp ? 'Cleaning…' : 'Run Housekeeping'}
          </button>
        </div>
      </div>

      {cleanupResult && (
        <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-800">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          {cleanupResult}
        </div>
      )}

      {/* Controls row */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-col sm:flex-row gap-3 flex-wrap">
        {/* File selector */}
        <div className="flex-1 min-w-0">
          <label className="block text-xs font-medium text-gray-500 mb-1">Log File</label>
          <div className="relative">
            <select
              value={selectedFile}
              onChange={(e) => setSelectedFile(e.target.value)}
              disabled={loadingFiles}
              className="w-full appearance-none bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {loadingFiles && <option>Loading…</option>}
              {files.map((f) => (
                <option key={f.filename} value={f.filename}>
                  {f.is_current ? `Today — ${f.filename}` : f.date} ({formatBytes(f.size_bytes)})
                </option>
              ))}
              {!loadingFiles && files.length === 0 && (
                <option value="">No log files found</option>
              )}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Level filter */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Level</label>
          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value as LevelFilter)}
            className="w-full appearance-none bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {LEVEL_FILTERS.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>

        {/* Lines */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Last N lines</label>
          <select
            value={maxLines}
            onChange={(e) => setMaxLines(Number(e.target.value))}
            className="w-full appearance-none bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {LINE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n} lines</option>
            ))}
          </select>
        </div>

        {/* Auto-refresh */}
        <div className="flex flex-col justify-end">
          <label className="block text-xs font-medium text-gray-500 mb-1">Auto-refresh</label>
          <button
            onClick={() => setAutoRefresh((v) => !v)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
              autoRefresh
                ? 'bg-emerald-700 text-white border-emerald-700'
                : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
            }`}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'On (10s)' : 'Off'}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search log lines…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>

      {/* Stats bar */}
      {selectedFileInfo && (
        <CollapsibleCard title="File Info" icon={<FileText className="w-4 h-4 text-gray-500" />}>
          <div className="px-6 py-4 flex items-center gap-4 flex-wrap text-xs text-gray-500">
            <span className="flex items-center gap-1 font-medium text-gray-700">
              <FileText className="w-3.5 h-3.5" />
              {selectedFileInfo.filename}
            </span>
            <span>{formatBytes(selectedFileInfo.size_bytes)}</span>
            {!loadingContent && (
              <span>{visibleLines.length.toLocaleString()} line{visibleLines.length !== 1 ? 's' : ''} shown</span>
            )}
            {levelFilter !== 'ALL' && (
              <LevelBadge level={levelFilter} />
            )}
            <button
              onClick={scrollToBottom}
              className="ml-auto text-emerald-600 hover:underline"
            >
              Jump to bottom ↓
            </button>
          </div>
        </CollapsibleCard>
      )}

      {/* Log content */}
      <div className="bg-gray-950 rounded-xl border border-gray-800 overflow-hidden">
        {error && (
          <div className="flex items-center gap-2 p-4 text-red-400 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {loadingContent && !rawContent && (
          <div className="flex items-center justify-center h-40 text-gray-500 text-sm">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            Loading logs…
          </div>
        )}

        {!loadingContent && !error && visibleLines.length === 0 && selectedFile && (
          <div className="flex items-center justify-center h-40 text-gray-500 text-sm">
            No log lines match your filters.
          </div>
        )}

        {!loadingContent && !error && !selectedFile && files.length === 0 && !loadingFiles && (
          <div className="flex items-center justify-center h-40 text-gray-500 text-sm">
            No log files found. Logs will appear here once the application generates output.
          </div>
        )}

        {visibleLines.length > 0 && (
          <div className="overflow-auto max-h-[600px] p-4">
            <pre className="text-xs font-mono leading-relaxed whitespace-pre-wrap break-all">
              {visibleLines.map((line, i) => (
                <div key={i} className={levelColor(line)}>
                  {line}
                </div>
              ))}
            </pre>
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-700">Available Log Files</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {files.map((f) => (
              <button
                key={f.filename}
                onClick={() => setSelectedFile(f.filename)}
                className={`w-full flex items-center justify-between px-5 py-3 text-left hover:bg-gray-50 transition-colors ${
                  selectedFile === f.filename ? 'bg-emerald-50' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <FileText className={`w-4 h-4 ${selectedFile === f.filename ? 'text-emerald-600' : 'text-gray-400'}`} />
                  <div>
                    <p className={`text-sm font-medium ${selectedFile === f.filename ? 'text-emerald-700' : 'text-gray-800'}`}>
                      {f.filename}
                    </p>
                    <p className="text-xs text-gray-400">{f.date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {f.is_current && (
                    <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded-full font-medium">
                      Current
                    </span>
                  )}
                  <span className="text-xs text-gray-400">{formatBytes(f.size_bytes)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
